import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAtom } from 'jotai';
import { Stack, Card, Group, Text, Switch, Select, Button, Divider } from '@mantine/core';
import { IconDiamond } from '@tabler/icons-react';
import RechargeOverlay from '@/components/payments/RechargeOverlay';
import { useAtom as useAtom2 } from 'jotai';
import { rechargeOverlayOpenAtom } from '@/stores/atoms/walletAtoms';
import PageHeader from '@/components/common/PageHeader';
import { useT } from '@/i18n';
import { activePageAtom, appConfigAtom, homeBackgroundImageAtom, homeTimeImageAtom } from '@/stores/atoms/uiAtoms';
import { themeAtom } from '@/stores/atoms/uiAtoms';
import { useSlideExit } from '@/renderer/utils/pageTransitions';
import { authUserAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { signOut, deleteCurrentUserAccount } from '@/utils/firebase';
import { logoutAuth } from '@/utils/userApi';
import { clearUserNamespace } from '@/renderer/storage/userLocal';
import { confirmDialog } from '@/utils/confirm';
import { notifications } from '@mantine/notifications';
import { sessionsAtom, recentSessionIdsAtom } from '@/stores/atoms/sessionAtoms';
import { chatsAtom, messagesMapAtom, currentChatIdAtom } from '@/stores/atoms/chatAtoms';
import { mineGamesAtom } from '@/stores/atoms/mineAtoms';
import { httpFetch } from '@/renderer/utils/http';
import { chatUiStyleAtom, chatBackgroundUrlAtom, chatPreferencesAtom, chatAvatarDataUrlAtom, chatNicknameAtom, chatStatusAtom, chatModelUrlAtom, chatModelNameAtom, chatModelApiKeyAtom, waPersonasAtom, waMessagesMapAtom, waCurrentPersonaIdAtom, waInitDoneAtom, waSelfNicknameAtom, waSelfDefineAtom, waSelfMemoryAtom, waMaxMsgCntAtom, waWallpaperMapAtom, waFeedsAtom, } from '@/stores/atoms/whatsappAtoms';
import { getImageApiBase } from '@/renderer/utils/session';
const Settings = () => {
    const t = useT();
    const [, setActivePage] = useAtom(activePageAtom);
    const [config, setConfig] = useAtom(appConfigAtom);
    const [, setSessions] = useAtom(sessionsAtom);
    const [, setRecentIds] = useAtom(recentSessionIdsAtom);
    const [, setChats] = useAtom(chatsAtom);
    const [, setMessagesMap] = useAtom(messagesMapAtom);
    const [, setCurrentChatId] = useAtom(currentChatIdAtom);
    const [, setMineGames] = useAtom(mineGamesAtom);
    const [authUser] = useAtom(authUserAtom);
    const [, setAuthUser] = useAtom(authUserAtom);
    const [, setAuthModalOpen] = useAtom(authModalOpenAtom);
    const [theme, setTheme] = useAtom(themeAtom);
    const [homeBg, setHomeBg] = useAtom(homeBackgroundImageAtom);
    const [homeTimeImg, setHomeTimeImg] = useAtom(homeTimeImageAtom);
    const [, setRechargeOpen] = useAtom2(rechargeOverlayOpenAtom);
    // WhatsApp atoms setters
    const [, setWaUiStyle] = useAtom(chatUiStyleAtom);
    const [, setWaBgUrl] = useAtom(chatBackgroundUrlAtom);
    const [, setWaPrefs] = useAtom(chatPreferencesAtom);
    const [, setWaAvatar] = useAtom(chatAvatarDataUrlAtom);
    const [, setWaNick] = useAtom(chatNicknameAtom);
    const [, setWaStatus] = useAtom(chatStatusAtom);
    const [, setWaModelUrl] = useAtom(chatModelUrlAtom);
    const [, setWaModelName] = useAtom(chatModelNameAtom);
    const [, setWaApiKey] = useAtom(chatModelApiKeyAtom);
    const [, setWaPersonas] = useAtom(waPersonasAtom);
    const [, setWaMsgsMap] = useAtom(waMessagesMapAtom);
    const [, setWaCurId] = useAtom(waCurrentPersonaIdAtom);
    const [, setWaInit] = useAtom(waInitDoneAtom);
    const [, setWaSelfNick] = useAtom(waSelfNicknameAtom);
    const [, setWaSelfDefine] = useAtom(waSelfDefineAtom);
    const [, setWaSelfMemory] = useAtom(waSelfMemoryAtom);
    const [, setWaMaxCnt] = useAtom(waMaxMsgCntAtom);
    const [, setWaWallpapers] = useAtom(waWallpaperMapAtom);
    const [, setWaFeeds] = useAtom(waFeedsAtom);
    const uploadImageDataUrl = async (dataUrl, filename) => {
        const api = getImageApiBase();
        const res = await httpFetch(`${api}/api/images`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: dataUrl, filename }),
        });
        if (!res.ok)
            throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json || json.success === false)
            throw new Error(String(json?.error || 'Upload failed'));
        return json.image_url || json.url;
    };
    const setTelemetry = (v) => {
        setConfig({ ...config, telemetryEnabled: v });
    };
    const setRetention = (days) => {
        setConfig({ ...config, dataRetentionDays: days });
    };
    const openPrivacy = () => {
        const url = 'https://mallorb.com/privacy';
        try {
            window.open(url, '_blank');
        }
        catch { }
    };
    const deleteAllLocalData = async () => {
        const ok = await confirmDialog({
            title: t('settings.deleteData'),
            message: t('settings.deleteDataConfirm'),
            labels: { confirm: t('confirm.ok'), cancel: t('confirm.cancel') },
        });
        if (!ok)
            return;
        try {
            if (!authUser) {
                setAuthModalOpen(true);
                return;
            }
            clearUserNamespace(authUser.uid);
            try {
                await logoutAuth();
            }
            catch { }
            try {
                await signOut();
            }
            catch { }
            try {
                localStorage.removeItem('firebase_uid');
            }
            catch { }
            // Do NOT remove 'anonymous_uid' so guest identity persists
            try {
                setAuthUser(null);
            }
            catch { }
            setSessions([]);
            setRecentIds([]);
            setChats([]);
            setMessagesMap({});
            setCurrentChatId(null);
            setMineGames([]);
            // Clear WhatsApp local data
            setWaUiStyle('default');
            setWaBgUrl('');
            setWaPrefs({ readReceipts: true, vibrate: true });
            setWaAvatar(undefined);
            setWaNick('user');
            setWaStatus('在线');
            setWaModelUrl('http://localhost:11434/v1');
            setWaModelName('qwen3-7b');
            setWaApiKey('');
            setWaPersonas([]);
            setWaMsgsMap({});
            setWaCurId(null);
            setWaInit(false);
            setWaSelfNick('user');
            setWaSelfDefine('');
            setWaSelfMemory('');
            setWaMaxCnt(7);
            setWaWallpapers({});
            setWaFeeds([]);
            notifications.show({ color: 'green', title: t('settings.deleted'), message: t('settings.deletedMsg'), withCloseButton: true });
        }
        catch (e) {
            notifications.show({ color: 'red', title: t('settings.deleteData'), message: String(e?.message || 'Error'), withCloseButton: true });
        }
    };
    const deleteAccount = async () => {
        const ok = await confirmDialog({
            title: t('settings.deleteAccount'),
            message: t('settings.deleteAccountConfirm'),
            labels: { confirm: t('confirm.ok'), cancel: t('confirm.cancel') },
        });
        if (!ok)
            return;
        try {
            if (!authUser) {
                setAuthModalOpen(true);
                return;
            }
            const uid = authUser.uid;
            await deleteCurrentUserAccount();
            // Clear local namespace and in-memory
            clearUserNamespace(uid);
            setSessions([]);
            setRecentIds([]);
            setChats([]);
            setMessagesMap({});
            setCurrentChatId(null);
            setMineGames([]);
            try {
                await logoutAuth();
            }
            catch { }
            try {
                await signOut();
            }
            catch { }
            try {
                localStorage.removeItem('firebase_uid');
            }
            catch { }
            // Do NOT remove 'anonymous_uid' so guest identity persists
            try {
                setAuthUser(null);
            }
            catch { }
            // Clear WhatsApp local data
            setWaUiStyle('default');
            setWaBgUrl('');
            setWaPrefs({ readReceipts: true, vibrate: true });
            setWaAvatar(undefined);
            setWaNick('user');
            setWaStatus('在线');
            setWaModelUrl('http://localhost:11434/v1');
            setWaModelName('qwen3-7b');
            setWaApiKey('');
            setWaPersonas([]);
            setWaMsgsMap({});
            setWaCurId(null);
            setWaInit(false);
            setWaSelfNick('user');
            setWaSelfDefine('');
            setWaSelfMemory('');
            setWaMaxCnt(7);
            setWaWallpapers({});
            setWaFeeds([]);
            notifications.show({ color: 'green', title: t('settings.deleted'), message: t('settings.deletedMsg'), withCloseButton: true });
            setActivePage('home');
        }
        catch (e) {
            const msg = e?.code === 'auth/requires-recent-login' ? t('settings.reauthRequired') : String(e?.message || 'Delete account failed');
            notifications.show({ color: 'red', title: t('settings.deleteAccount'), message: msg, withCloseButton: true });
        }
    };
    const { startExit, containerStyle, underlayStyle, panelStyle } = useSlideExit();
    return (_jsxs("div", { style: containerStyle, children: [_jsx("div", { "aria-hidden": true, style: underlayStyle }), _jsx("div", { style: panelStyle, children: _jsxs(Stack, { p: "sm", gap: "sm", style: { height: '100%', overflow: 'auto' }, children: [_jsx(PageHeader, { title: t('settings.title'), onBack: () => startExit(() => setActivePage('home')) }), _jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsx(Text, { fw: 700, children: "\u5916\u89C2\u4E0E\u9996\u9875" }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: "\u4E3B\u9898" }), _jsx(Select, { data: [{ value: 'auto', label: '自动' }, { value: 'light', label: '浅色' }, { value: 'dark', label: '深色' }], value: theme, onChange: (v) => setTheme(v || 'auto'), w: 160 })] }), _jsxs(Group, { align: "center", justify: "space-between", children: [_jsxs("div", { children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: "\u9996\u9875\u80CC\u666F\u56FE" }), _jsx(Text, { size: "xs", style: { color: 'var(--app-text-secondary)' }, children: "\u652F\u6301\u672C\u5730\u56FE\u7247\uFF0C\u5EFA\u8BAE\u5C3A\u5BF8\u63A5\u8FD1\u5C4F\u5E55\u6BD4\u4F8B" })] }), _jsxs(Group, { gap: 8, children: [_jsx("input", { type: "file", accept: "image/*", onChange: async (e) => {
                                                            const file = e.currentTarget.files?.[0];
                                                            if (!file)
                                                                return;
                                                            try {
                                                                const dataUrl = await new Promise((resolve, reject) => {
                                                                    const reader = new FileReader();
                                                                    reader.onload = () => resolve(String(reader.result || ''));
                                                                    reader.onerror = reject;
                                                                    reader.readAsDataURL(file);
                                                                });
                                                                const url = await uploadImageDataUrl(dataUrl, file.name);
                                                                setHomeBg(url);
                                                                notifications.show({ color: 'green', title: '已更新', message: '首页背景图已更改', withCloseButton: true });
                                                            }
                                                            catch (err) {
                                                                notifications.show({ color: 'red', title: '更新失败', message: String(err?.message || '上传失败'), withCloseButton: true });
                                                            }
                                                        } }), _jsx(Button, { variant: "light", onClick: () => setHomeBg(null), children: "\u6062\u590D\u9ED8\u8BA4" })] })] }), _jsxs(Group, { align: "center", justify: "space-between", children: [_jsxs("div", { children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: "\u65F6\u95F4\u533A\u57DF\u56FE\u7247" }), _jsx(Text, { size: "xs", style: { color: 'var(--app-text-secondary)' }, children: "\u7528\u4E8E\u9996\u9875\u65F6\u95F4\u533A\u57DF\u7684\u80CC\u666F\uFF0C\u5EFA\u8BAE\u7EAF\u8272\u6216\u7B80\u6D01\u56FE\u7247" })] }), _jsxs(Group, { gap: 8, children: [_jsx("input", { type: "file", accept: "image/*", onChange: async (e) => {
                                                            const file = e.currentTarget.files?.[0];
                                                            if (!file)
                                                                return;
                                                            try {
                                                                const dataUrl = await new Promise((resolve, reject) => {
                                                                    const reader = new FileReader();
                                                                    reader.onload = () => resolve(String(reader.result || ''));
                                                                    reader.onerror = reject;
                                                                    reader.readAsDataURL(file);
                                                                });
                                                                const url = await uploadImageDataUrl(dataUrl, file.name);
                                                                setHomeTimeImg(url);
                                                                notifications.show({ color: 'green', title: '已更新', message: '时间区域图片已更改', withCloseButton: true });
                                                            }
                                                            catch (err) {
                                                                notifications.show({ color: 'red', title: '更新失败', message: String(err?.message || '上传失败'), withCloseButton: true });
                                                            }
                                                        } }), _jsx(Button, { variant: "light", onClick: () => setHomeTimeImg(null), children: "\u6062\u590D\u9ED8\u8BA4" })] })] })] }) }), _jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsx(Text, { fw: 700, children: "\u8D26\u6237\u4E0E\u4F1A\u5458" }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: "\u5145\u503C\u4F1A\u5458" }), _jsx(Button, { leftSection: _jsx(IconDiamond, { size: 16 }), variant: "light", onClick: () => setRechargeOpen(true), children: "\u53BB\u5145\u503C" })] })] }) }), _jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsx(Text, { fw: 700, children: t('settings.privacy') }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: t('settings.telemetry') }), _jsx(Switch, { checked: Boolean(config.telemetryEnabled), onChange: (e) => setTelemetry(e.currentTarget.checked) })] }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: t('settings.dataRetention') }), _jsx(Select, { data: [{ value: '0', label: t('settings.retention.never') }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: '365', label: '1 year' }], value: config?.dataRetentionDays != null ? String(config.dataRetentionDays) : '0', onChange: (v) => setRetention(v ? Number(v) : null), w: 160 })] }), _jsx(Group, { children: _jsx(Button, { variant: "light", onClick: openPrivacy, children: t('settings.privacyPolicy') }) })] }) }), _jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsx(Text, { fw: 700, children: t('settings.dataControl') }), _jsx(Text, { size: "sm", style: { color: 'var(--app-text-secondary)' }, children: t('settings.dataControlDesc') }), _jsxs(Group, { children: [_jsx(Button, { color: "red", variant: "light", onClick: deleteAllLocalData, children: t('settings.deleteData') }), _jsx(Button, { color: "red", onClick: deleteAccount, children: t('settings.deleteAccount') })] }), _jsx(Divider, { my: "xs" }), _jsx(Text, { size: "xs", style: { color: 'var(--app-text-secondary)' }, children: t('settings.usCompliance') })] }) }), _jsx(RechargeOverlay, {})] }) })] }));
};
export default Settings;
